import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { documentAPI, authAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  Upload,
  FileText,
  Key,
  Send,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

function SignDocument() {
  const [formData, setFormData] = useState({
    documentName: "",
    documentData: "",
    signature: "",
    documentType: "PDF",
    description: "",
    tags: "",
    category: "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Details, 3: Sign
  const [hasPublicKey, setHasPublicKey] = useState(true);
  const [checkingKey, setCheckingKey] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkPublicKey();
  }, []);

  const checkPublicKey = async () => {
    try {
      const response = await authAPI.getProfile();
      setHasPublicKey(!!response.data.publicKey);
    } catch (error) {
      console.error("Error checking public key:", error);
      setHasPublicKey(false);
    } finally {
      setCheckingKey(false);
    }
  };

  const quickSetupPublicKey = async () => {
    setGeneratingKey(true);
    try {
      // Generate a new public key
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 15);
      const publicKey = `PUB_${timestamp}_${randomStr}`;

      // Save it to the user's profile
      await authAPI.updatePublicKey(publicKey);

      // Update local state
      setHasPublicKey(true);
      toast.success("✅ Public key generated and saved successfully!");
    } catch (error) {
      console.error("Error setting up public key:", error);
      toast.error("Failed to set up public key. Please try again.");
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 50 * 1024 * 1024) {
        // 50MB limit
        toast.error("File size must be less than 50MB");
        return;
      }

      setFile(selectedFile);
      setFormData({
        ...formData,
        documentName: selectedFile.name,
        documentType: selectedFile.type.includes("pdf") ? "PDF" : "OTHER",
      });

      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setFormData((prev) => ({
          ...prev,
          documentData: base64,
        }));
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const generateSignature = () => {
    // Simple signature generation for demo purposes
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2);
    const signature = `${formData.documentName}-${timestamp}-${randomString}`;

    setFormData({
      ...formData,
      signature,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        tags: formData.tags
          ? formData.tags.split(",").map((tag) => tag.trim())
          : [],
      };

      await documentAPI.signDocument(submitData);
      toast.success("Document signed successfully!");
      navigate("/documents");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Failed to sign document";

      if (errorMessage.includes("public key not found")) {
        toast.error(
          "Public key not found. Please set up your public key in your profile first.",
          { autoClose: 8000 }
        );
        setTimeout(() => navigate("/profile"), 2000);
      } else {
        toast.error(errorMessage);
      }
      console.error("Sign document error:", error);
    } finally {
      setLoading(false);
    }
  };

  const canProceedToStep2 = file && formData.documentData;
  const canProceedToStep3 = formData.documentName && formData.documentData;
  const canSubmit =
    formData.signature && formData.documentName && formData.documentData;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Sign New Document
          </h1>
          <p className="mt-2 text-gray-600">
            Upload and digitally sign your documents securely
          </p>
        </div>

        {/* Public Key Warning */}
        {!checkingKey && !hasPublicKey && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Public Key Required
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p className="mb-3">
                    You need to set up your public key before you can sign
                    documents.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={quickSetupPublicKey}
                      disabled={generatingKey}
                      className="inline-flex items-center space-x-2 px-3 py-2 border border-yellow-300 rounded-md text-sm font-medium text-yellow-800 bg-yellow-100 hover:bg-yellow-200 disabled:opacity-50"
                    >
                      {generatingKey ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                      ) : (
                        <Key className="h-4 w-4" />
                      )}
                      <span>
                        {generatingKey ? "Setting up..." : "Quick Setup"}
                      </span>
                    </button>
                    <button
                      onClick={() => navigate("/profile")}
                      className="inline-flex items-center space-x-1 text-sm font-medium text-yellow-800 underline hover:text-yellow-900"
                    >
                      <span>Or manage in Profile</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-center max-w-lg mx-auto space-x-8">
              <li className="relative flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                      step >= 1
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-500"
                    }`}
                  >
                    <span className="text-sm font-medium">1</span>
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-900 text-center">
                    Upload
                    <br />
                    Document
                  </span>
                </div>
              </li>

              <li className="relative flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                      step >= 2
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-500"
                    }`}
                  >
                    <span className="text-sm font-medium">2</span>
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-900 text-center">
                    Document
                    <br />
                    Details
                  </span>
                </div>
              </li>

              <li className="relative flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                      step >= 3
                        ? "bg-primary-600 border-primary-600 text-white"
                        : "bg-white border-gray-300 text-gray-500"
                    }`}
                  >
                    <span className="text-sm font-medium">3</span>
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-900 text-center">
                    Sign
                    <br />
                    Document
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Step 1: Upload Document */}
            {step === 1 && (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
                <div className="max-w-md mx-auto text-center">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 hover:border-gray-400 transition-colors">
                    <Upload className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <div className="mb-4">
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                      >
                        Choose File
                      </label>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".pdf,.txt,.doc,.docx"
                        onChange={handleFileChange}
                      />
                    </div>
                    <p className="text-sm text-gray-500">
                      PDF, TXT, DOC up to 50MB
                    </p>
                  </div>

                  {file && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-center space-x-2">
                        <FileText className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {file.name}
                        </span>
                        <span className="text-xs text-green-600">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                    </div>
                  )}

                  {canProceedToStep2 && (
                    <div className="mt-8">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        className="btn-primary"
                      >
                        Next: Document Details
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Document Details */}
            {step === 2 && (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Document Details
                </h3>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="documentName" className="label">
                      Document Name *
                    </label>
                    <input
                      type="text"
                      id="documentName"
                      name="documentName"
                      required
                      className="input-field"
                      value={formData.documentName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          documentName: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label htmlFor="documentType" className="label">
                      Document Type
                    </label>
                    <select
                      id="documentType"
                      name="documentType"
                      className="input-field"
                      value={formData.documentType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          documentType: e.target.value,
                        })
                      }
                    >
                      <option value="PDF">PDF</option>
                      <option value="TEXT">Text</option>
                      <option value="DOC">Document</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="category" className="label">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      className="input-field"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                    >
                      <option value="">Select a category</option>
                      <option value="Contract">Contract</option>
                      <option value="Agreement">Agreement</option>
                      <option value="Invoice">Invoice</option>
                      <option value="Legal">Legal Document</option>
                      <option value="Personal">Personal</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="description" className="label">
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={3}
                      className="input-field"
                      placeholder="Brief description of the document..."
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div>
                    <label htmlFor="tags" className="label">
                      Tags
                    </label>
                    <input
                      type="text"
                      id="tags"
                      name="tags"
                      className="input-field"
                      placeholder="Enter tags separated by commas (e.g., important, confidential)"
                      value={formData.tags}
                      onChange={(e) =>
                        setFormData({ ...formData, tags: e.target.value })
                      }
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Separate multiple tags with commas
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="btn-secondary"
                  >
                    Back
                  </button>
                  {canProceedToStep3 && (
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="btn-primary"
                    >
                      Next: Sign Document
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Sign Document */}
            {step === 3 && (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Digital Signature
                </h3>

                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Key className="h-5 w-5 text-blue-400" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">
                          Digital Signature Information
                        </h3>
                        <div className="mt-2 text-sm text-blue-700">
                          <p>
                            Your document will be digitally signed using
                            cryptographic algorithms. This ensures authenticity
                            and integrity.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="signature" className="label">
                      Digital Signature *
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                      <input
                        type="text"
                        id="signature"
                        name="signature"
                        required
                        className="input-field rounded-r-none"
                        placeholder="Generate or enter your digital signature"
                        value={formData.signature}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            signature: e.target.value,
                          })
                        }
                      />
                      <button
                        type="button"
                        onClick={generateSignature}
                        className="relative -ml-px inline-flex items-center px-4 py-2 border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-r-md"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        Generate
                      </button>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Click "Generate" to create a unique digital signature for
                      this document
                    </p>
                  </div>

                  {formData.signature && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">
                            Signature Ready
                          </h3>
                          <div className="mt-2 text-sm text-green-700">
                            <p>
                              Your document is ready to be signed and submitted.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="btn-secondary"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!canSubmit || loading || !hasPublicKey}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    <span>{loading ? "Signing..." : "Sign Document"}</span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default SignDocument;
